import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Product } from '../data/product';

export interface CartItem {
  id: string;
  name: string;
  price: string; // keep string ($xx)
  image: string;
  priceId?: string;
}

interface CartContextValue {
  items: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number; // numeric total
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function parsePrice(price: string): number { return parseFloat(price.replace(/[^0-9.]/g,'')) || 0; }

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
export function formatCurrency(amount: number){ return currencyFormatter.format(amount); }

// Buy One Get One Free: for every 2 items the cheaper ones are free (free the
// floor(N/2) cheapest). Single source of truth for the customer-facing estimate;
// mirrors the server-side coupon math in api/create-checkout-session.js.
export function bogoDiscount(prices: number[]): number {
  const asc = [...prices].sort((a,b)=> a-b);
  const freeCount = Math.floor(asc.length / 2);
  let off = 0;
  for (let i = 0; i < freeCount; i++) off += asc[i];
  return off;
}
export function bogoDiscountForItems(items: { price: string }[]): number {
  return bogoDiscount(items.map(i => parsePrice(i.price)));
}

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const raw = localStorage.getItem('cart');
      if (raw) {
        const parsed: any[] = JSON.parse(raw);
        // Normalize any older carts that had qty and no priceId
        return parsed.map(it => ({
          id: it.id,
          name: it.name,
          price: it.price,
          image: it.image,
          priceId: it.priceId, // may be undefined for legacy items
        })).filter(Boolean);
      }
    } catch(_) {}
    return [];
  });

  useEffect(() => { try { localStorage.setItem('cart', JSON.stringify(items)); } catch(_) {} }, [items]);

  const addToCart = useCallback((product: Product) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === product.id);
      if (existing) return prev; // ignore duplicate add
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.image, priceId: product.priceId }];
    });
  }, []);

  const removeFromCart = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.length;
  const totalPrice = items.reduce((sum,i) => sum + parsePrice(i.price), 0);

  return (
    <CartContext.Provider value={{ items, addToCart, removeFromCart, clearCart, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export function useCart(){
  const ctx = useContext(CartContext);
  if(!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}